import { useEffect, useRef } from "react";
import type { ScheduleProposalActorRole, ScheduleProposalResponse } from "../../types/gigTypes";
import { formatDateTime, formatDateTimeInGigTimeZone } from "../../utils/DateUtils";

const actorLabel = (actorRole: ScheduleProposalActorRole, perspective: "teen" | "adult") => {
  if (perspective === "teen") {
    return actorRole === "TEEN" ? "You" : "Gig poster";
  }
  return actorRole === "TEEN" ? "Teen" : "You";
};

const isConfirmationProposal = (
  proposal: ScheduleProposalResponse,
  previousProposal: ScheduleProposalResponse | undefined,
) =>
  previousProposal
  && proposal.actorRole !== previousProposal.actorRole
  && proposal.proposedStartTime === previousProposal.proposedStartTime
  && proposal.proposedEndTime === previousProposal.proposedEndTime;

const proposalLabel = (
  proposal: ScheduleProposalResponse,
  previousProposal: ScheduleProposalResponse | undefined,
  perspective: "teen" | "adult",
) => {
  if (isConfirmationProposal(proposal, previousProposal)) {
    return `${actorLabel(proposal.actorRole, perspective)} confirmed the proposed time`;
  }
  return `${actorLabel(proposal.actorRole, perspective)} proposed a time`;
};

type ScheduleProposalTimelineProps = {
  proposals?: ScheduleProposalResponse[] | null;
  perspective: "teen" | "adult";
  timeZone?: string | null;
  emptyMessage?: string;
};

const ScheduleProposalTimeline = ({
  proposals,
  perspective,
  timeZone,
  emptyMessage = "No schedule proposals yet.",
}: ScheduleProposalTimelineProps) => {
  const safeProposals = proposals ?? [];
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }, [safeProposals.length]);

  if (safeProposals.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background px-4 py-4 text-sm text-myteengig-text-muted">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="max-h-[28rem] overflow-y-auto pr-1 sm:pr-2"
    >
      <div className="grid min-w-0 gap-3">
        {safeProposals.map((proposal, index) => {
          const isLatest = index === safeProposals.length - 1;
          const previousProposal = index > 0 ? safeProposals[index - 1] : undefined;
          return (
            <div
              key={proposal.id}
              className={`min-w-0 rounded-2xl border px-3 py-3 sm:px-4 sm:py-4 ${
                isLatest
                  ? "border-myteengig-primary/30 bg-[linear-gradient(180deg,_rgba(236,252,203,0.8),_rgba(255,255,255,1))]"
                  : "border-myteengig-border bg-myteengig-background"
              }`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <p className="min-w-0 text-sm font-semibold text-myteengig-text-primary">
                  {proposalLabel(proposal, previousProposal, perspective)}
                </p>
                {isLatest ? (
                  <span className="inline-flex w-fit rounded-full border border-myteengig-primary/30 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-myteengig-primary">
                    Latest
                  </span>
                ) : null}
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div className="rounded-xl bg-white/80 px-3 py-2 text-center sm:text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-myteengig-text-muted">Start</p>
                  <p className="mt-1 text-sm font-medium text-myteengig-text-primary">{formatDateTimeInGigTimeZone(proposal.proposedStartTime, timeZone)}</p>
                </div>
                <div className="rounded-xl bg-white/80 px-3 py-2 text-center sm:text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-myteengig-text-muted">End</p>
                  <p className="mt-1 text-sm font-medium text-myteengig-text-primary">{formatDateTimeInGigTimeZone(proposal.proposedEndTime, timeZone)}</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-myteengig-text-muted">Submitted {formatDateTime(proposal.createdAt)}</p>
              <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">
                {proposal.note?.trim() ? proposal.note : "No timing note was added."}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScheduleProposalTimeline;
